import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { connectMessaging, onMessagingEvent, getPresence, startFollow, stopFollow } from '@/lib/messagingSocket';
import { Eye, UserCheck, ChevronDown } from 'lucide-react';

const initials = (name) => (name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
const COLORS = ['bg-indigo-500', 'bg-cyan-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-violet-500'];
const colorFor = (id) => COLORS[(id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length];

// Shows the teammates currently viewing this same object, with a click-to-follow
// control. Data is never shared — following only syncs navigation/context (D6).
export default function PresenceViewers({ objectType, objectId, following, onFollow, onStopFollow }) {
  const { user } = useAuth();
  const me = user?.user_id;
  const [presence, setPresence] = useState(getPresence());
  const [rosterOpen, setRosterOpen] = useState(false);

  useEffect(() => {
    connectMessaging();
    setPresence(getPresence());
    const off = onMessagingEvent((evt) => { if (evt.type === 'presence') setPresence(evt.presence || {}); });
    return () => off();
  }, []);

  const viewers = Object.entries(presence)
    .filter(([uid, p]) => uid !== me && p?.viewing && p.viewing.object_type === objectType && String(p.viewing.object_id) === String(objectId))
    .map(([uid, p]) => ({ uid, name: p.name, avatarUrl: p.avatarUrl, role: p.role, viewing: p.viewing }));

  if (viewers.length === 0) return null;

  return (
    <div className="relative flex items-center gap-2" data-testid="presence-viewers">
      <button type="button" onClick={() => setRosterOpen(o => !o)}
        className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" data-testid="presence-roster-toggle">
        <Eye className="w-3 h-3" /> {viewers.length} viewing <ChevronDown className={`w-3 h-3 transition-transform ${rosterOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className="flex items-center -space-x-2">
        {viewers.slice(0, 5).map(v => {
          const isFollowing = following === v.uid;
          return (
            <div key={v.uid} className="relative group/vc">
              <button
                type="button"
                onClick={() => (isFollowing ? (stopFollow(v.uid), onStopFollow?.()) : (startFollow(v.uid), onFollow?.(v)))}
                data-testid={`presence-viewer-${v.uid}`}
                className="relative hover:scale-110 transition-transform block"
              >
                {v.avatarUrl
                  ? <img src={v.avatarUrl} alt={v.name || ''} className={`w-7 h-7 rounded-full object-cover ring-2 ${isFollowing ? 'ring-cyan-400' : 'ring-background'}`} />
                  : <span className={`w-7 h-7 rounded-full ${colorFor(v.uid)} text-white text-[10px] font-semibold flex items-center justify-center ring-2 ${isFollowing ? 'ring-cyan-400' : 'ring-background'}`}>{initials(v.name)}</span>}
                {isFollowing && <UserCheck className="w-3 h-3 absolute -bottom-1 -right-1 text-cyan-300 bg-background rounded-full" />}
              </button>
              <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 w-max max-w-[220px] rounded-lg border border-border/60 bg-popover shadow-lg px-3 py-2 opacity-0 group-hover/vc:opacity-100 transition-opacity" data-testid={`presence-card-${v.uid}`}>
                <p className="text-xs font-semibold text-foreground">{v.name}{v.uid === me ? ' (you)' : ''}</p>
                {v.role && <p className="text-[10px] text-muted-foreground capitalize">{String(v.role).replace(/_/g, ' ')}</p>}
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {v.viewing?.label ? <>Viewing <span className="text-foreground/80">{v.viewing.label}</span></> : 'Viewing this page'}
                </p>
                <p className="text-[9px] text-cyan-400/80 mt-1">{isFollowing ? 'Click to stop following' : 'Click to follow'}</p>
              </div>
            </div>
          );
        })}
      </div>

      {rosterOpen && (
        <div className="absolute right-0 top-full mt-2 z-50 w-64 rounded-xl border border-border/60 bg-popover shadow-xl p-2" data-testid="presence-roster-panel">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider px-1 pb-1">Who's here · {viewers.length}</p>
          <div className="max-h-64 overflow-y-auto space-y-0.5">
            {viewers.map(v => {
              const isFollowing = following === v.uid;
              return (
                <div key={v.uid} className="flex items-center gap-2 px-1.5 py-1 rounded-lg hover:bg-muted/40" data-testid={`roster-row-${v.uid}`}>
                  {v.avatarUrl
                    ? <img src={v.avatarUrl} alt={v.name || ''} className="w-6 h-6 rounded-full object-cover shrink-0" />
                    : <span className={`w-6 h-6 rounded-full ${colorFor(v.uid)} text-white text-[9px] font-semibold flex items-center justify-center shrink-0`}>{initials(v.name)}</span>}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-foreground truncate">{v.name}{v.uid === me ? ' (you)' : ''}</p>
                    <p className="text-[9px] text-muted-foreground truncate">
                      {v.role ? <span className="capitalize">{String(v.role).replace(/_/g, ' ')}</span> : null}
                      {v.viewing?.label ? `${v.role ? ' · ' : ''}${v.viewing.label}` : ''}
                    </p>
                  </div>
                  {v.uid !== me && (
                    <button type="button"
                      onClick={() => (isFollowing ? (stopFollow(v.uid), onStopFollow?.()) : (startFollow(v.uid), onFollow?.(v)))}
                      className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 transition-colors ${isFollowing ? 'border-cyan-400 text-cyan-300 bg-cyan-500/10' : 'border-border/60 text-muted-foreground hover:text-foreground'}`}
                      data-testid={`roster-follow-${v.uid}`}>
                      {isFollowing ? 'Stop' : 'Follow'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
